// src/hooks/useProducts.js
import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { applyTierDiscount } from '../config/constants';
import { useAuth } from './useAuth';

export function useProducts(userTier = 'standard', tierDiscount = 0) {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Don't query until auth is resolved and user is signed in
    if (authLoading || !user) {
      if (!authLoading) setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(
      collection(db, 'products'),
      where('active', '==', true),
      orderBy('sortOrder', 'asc')
    );

    const unsubscribe = onSnapshot(
      q, 
      { includeMetadataChanges: true },
      (snapshot) => {
        const productList = snapshot.docs.map(doc => {
          const data = doc.data();
          const discountedPrice = applyTierDiscount(data.wholesalePrice, tierDiscount);
          return {
            id: doc.id,
            ...data,
            originalWholesalePrice: data.wholesalePrice,
            wholesalePrice: Math.round(discountedPrice * 100) / 100
          };
        });
        setProducts(productList);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching products:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tierDiscount, user, authLoading]);

  return { products, loading, error };
}

// Hook for admin to get all products including inactive
export function useAllProducts() {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading || !user) {
      if (!authLoading) setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(collection(db, 'products'));

    const unsubscribe = onSnapshot(
      q, 
      { includeMetadataChanges: true },
      (snapshot) => {
        const productList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        productList.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setProducts(productList);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching products:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading]);

  return { products, loading, error };
}
